import { useCallback, useMemo } from 'react';
import { isTextarea, isCustomUrlProperty } from 'customer-data-objects/property/PropertyIdentifier';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { useProperty } from '../v2/hooks/useProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import { useGetIsUngated } from '../v2/hooks/useGetIsUngated';
import { gate } from 'hub-http/gates';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import ExpandingTextAreaAdapter from '../adapters/ExpandingTextArea';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { usePropertyValueFormatting } from '../v2/hooks/usePropertyValueFormatting';
import { formatUrlValue } from './url/urlUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const PropertyInputExpandableText = props => {
  const {
    onChange,
    inputRef,
    defaultValue
  } = props;
  const property = useProperty();
  const value = usePropertyValue();
  const getIsUngated = useGetIsUngated();
  const isUngatedForPropertyInputNormalization = getIsUngated(gate('CRM:PropMgmt:PropertyInputNormalization'));
  const {
    onChange: normalizeInputOnChange,
    normalizationRule
  } = usePropertyValueFormatting({
    onChange
  });
  useComponentMountMetric('PropertyInputExpandableText');
  const isCustomUrl = isCustomUrlProperty(property) && getIsUngated(gate('CRM:PropMgmt:UrlPropertyKind'));
  const handleChange = useCallback(event => {
    const newValue = isCustomUrl ? formatUrlValue(value || '', event.target.value) : event.target.value;
    if (!isTextarea(property)) {
      if (event.target.value.includes('\n')) {
        const withoutNewlines = newValue.replace(/\n/g, '');
        if (withoutNewlines === value) {
          return; // no change, don't call handler
        }

        // prevent newlines if not a textarea, for why we're doing this see https://git.hubteam.com/HubSpot/CRM-Issues/issues/4221
        return normalizeInputOnChange(Object.assign({}, event, {
          target: Object.assign({}, event.target, {
            value: withoutNewlines
          })
        }));
      }
    }
    return normalizeInputOnChange(Object.assign({}, event, {
      target: Object.assign({}, event.target, {
        value: newValue
      })
    }));
  }, [isCustomUrl, normalizeInputOnChange, property, value]);
  const normalizationRuleMessage = useMemo(() => {
    if (!isUngatedForPropertyInputNormalization) {
      return null;
    }
    if (normalizationRule === 'ALPHANUMERIC') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.NormalizationMessage.numbersOnly"
      });
    }
    if (normalizationRule === 'SPECIAL_CHARACTERS') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.NormalizationMessage.dontAllowSpecialCharacters"
      });
    }
    return null;
  }, [normalizationRule, isUngatedForPropertyInputNormalization]);
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !normalizationRuleMessage,
    title: normalizationRuleMessage,
    open: Boolean(normalizationRuleMessage),
    children: /*#__PURE__*/_jsx(ExpandableText, {
      buttonAlign: "left",
      hideButtonCaret: true,
      children: /*#__PURE__*/_jsx(ExpandingTextAreaAdapter, Object.assign({}, getTransferrableProps(props), {
        value: value === null ? defaultValue || '' : value,
        inputRef: inputRef,
        onChange: handleChange,
        shrink: true
      }))
    })
  });
};
export default PropertyInputExpandableText;